<template>
  <div id="import-posts-container">
    <div class="bg-white border p-6 rounded-xl mb-20 lg:mb-0">
      <h2 class="font-semibold text-2xl text-neutral-800">{{ $t('Import') }}</h2>
      <p class="text-neutral-600 mt-1">{{ $t('Use the fields below to import data from third-party platforms...') }}</p>

      <TabGroup class="mt-5" :tabs="importTabs" :value="activeTab" @input="switchTab" />

      <div class="sso-webhook-style">
        <h4 class="mt-8 font-semibold text-neutral-800">{{ $t('Upload CSV') }}</h4>
        <p class="text-neutral-600 mt-1">{{ $t('Upload your .CSV to populate your board with') }} {{ tabLabel }}. {{ $t('You can download a sample .CSV file') }} <a :href="downloadLink" target="_blank" class="download-template-link">{{ $t('clicking here') }}</a>.</p>

        <div
            enctype="multipart/form-data"
            accept-charset="UTF-8"
            class="mt-4"
            id="custom-designs"
        >
            <input name="utf8" type="hidden" value="✓" />
            <input type="hidden" name="authenticity_token" :value="this.form_token" />
            <div class="flex items-center mt-3">
              <button
                type="button"
                class="inline-flex items-center justify-center px-8 py-2 border border-transparent rounded-lg focus:outline-none text-xs btn-color"
                @click="$refs.csvFile.click()"
              >
                {{ $t('Choose file') }}
              </button>
              <span class="ml-3 text-sm text-neutral-400">{{ fileName || $t('No file selected') }}</span>
              <input type="file" name="csv" @change="getFile" id="upload-file" ref="csvFile" class="hidden" />
            </div>
            <div class="progress mt-3">
              <div class="progress-bar" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
            </div>

            <h4 class="mt-8 font-semibold text-neutral-800">{{ $t('Consent') }}</h4>
            <p class="settings-section__desc">{{ $t('Please confirm before importing.') }}</p>
            <div class="settings-card mt-2">
              <div class="settings-card__row">
                <label class="settings-card__toggle">
                  <input
                    type="checkbox"
                    class="settings-card__checkbox"
                    value=""
                    id="aggrement-input"
                  />
                  <div class="settings-card__row-content">
                    <span class="settings-card__row-title">{{ $t("I confirm I have the user's consent...") }}</span>
                    <span class="settings-card__row-desc">{{ $t('You must have consent from the users whose data you are importing.') }}</span>
                  </div>
                </label>
                <p id="aggrement-error" class="aggrement-error"></p>
              </div>
            </div>

            <button
                name="Import"
                class="btn-main mt-6 text-center justify-center"
                v-if="user_signed_in"
                @click="importCSV"
                :style="{maxWidth:'190px'}"
                id="import-post-btn"
                >
                <svg v-if="isLoading" class="animate-spin h-15 w-5 mr-3" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" fill="#EA4F36" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t('Import') }}
            </button>
        </div>
      </div>

      <div v-if="errors" class="mt-8" id="import-posts-error">
        <div class="mb-5 bg-yellow-100 border border-yellow-400 p-4 rounded-xl text-yellow-900 text-sm">
          <p><strong>{{ $t('There were errors while importing the CSV') }}</strong></p>
          <p>{{ $t('Please remember:') }}</p>
          <ul class="suggestions">
            <li>{{ $t('Use double quotes in fields that contains commas') }}</li>
            <li>{{ $t('When double quotes are required use them to open and close a content field') }}</li>
            <li>{{ $t('In case of doubts in the format, please') }} <a :href="downloadLink" class="download-template-link" target="_blank">{{ $t('clicking here') }}</a> {{ $t('to download the example') }}</li>
          </ul>
        </div>
        <h4 class="font-semibold text-neutral-800">{{ $t('Idea import errors') }}</h4>
        <p v-for="key in Object.keys(errors)" :key="key" class="text-neutral-600 mt-1">
          {{ errors[key] }}
        </p>
        <div class="flex mt-5 flex-row justify-between" id="posts-faild-records">
          <button
              name="Import"
              class="btn-secondary text-center block"
              v-if="user_signed_in && this.failed_records"
              @click="downloadFailedRecords"
              >
              {{ $t('Download Failed Records') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TabGroup from "../components/tab_group";

export default {
  components: { TabGroup },
  props: ["user_signed_in","action","token"],
  data: function () {
    return {
      form_token: this.token,
      form_action: this.action,
      file: null,
      errors: null,
      records: null,
      failed_records: null,
      isLoading: false,
      activeTab: 'idea',
      fileName: null
    }
  },
  computed: {
    importTabs: function () {
      return [
        { name: 'idea', label: this.$t('Ideas') },
        { name: 'comment', label: this.$t('Comments') },
        { name: 'upvote', label: this.$t('Upvotes') },
      ];
    },
    tabLabel: function () {
      var labels = { idea: 'ideas', comment: 'comments', upvote: 'upvotes' };
      return labels[this.activeTab];
    },
    downloadLink: function () {
      var links = { idea: '/files/idea-import.csv', comment: '/files/comment-import.csv', upvote: '/files/upvote-import.csv' };
      return links[this.activeTab];
    }
  },
  methods: {
    switchTab: function (tab) {
      this.activeTab = tab;
      this.file = null;
      this.fileName = null;
      if (this.$refs.csvFile) this.$refs.csvFile.value = null;
    },
    importCSV: function () {
      if (document.getElementById('aggrement-input').checked) {
        this.isLoading = true;
        this.errors = null;
        var data = new FormData();
        data.append("csv", this.file);
        data.append(this.activeTab, this.file);
        Rails.ajax({
          url: this.action,
          type: "POST",
          data: data,
          dataType: "json",
          success: (data) => {
            this.isLoading = false;
            this.errors = data.errors;
            this.records = data.records_imported;
            this.failed_records = data.failed_records;
          },
          error: (data) => {
            this.isLoading = false;
            this.errors = [(data && data.error) || this.$t("The import failed. Please check the file and try again.")];
          },
        });
      } else {
        document.getElementById('aggrement-error').innerHTML = "*please confirm first.";
      }
    },
    downloadFailedRecords: function () {
      var csvContent = "data:text/csv;charset=utf-8,";
      csvContent += [
        ...this.failed_records.map(item => Object.values(item).join(","))
      ]
      .join("\n")
      .replace(/(^\[)|(\]$)/gm, "");
      var data = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", "errors.csv");
      link.click();
    },
    getFile: function () {
      this.file = this.$refs.csvFile.files[0];
      this.fileName = this.file.name;
      this.$refs.csvFile.value = null;
    }
  },
};
</script>
<style scoped>
  .download-template-link {
    text-decoration: underline;
    color: rebeccapurple;
  }
  .import-errors-alert {
    flex-direction: column;
    align-items: flex-start;
  }
  .import-errors-alert .suggestions{
    list-style-type: circle;
    margin-left: 25px;
  }


  .btn-color {
    color: #5c5c5c;
    background-color: #d3d3d361;
  }

  .settings-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
  }

  .settings-card__row {
    padding: 14px 16px;
    border-bottom: 1px solid #f3f4f6;
  }

  .settings-card__row:last-child {
    border-bottom: none;
  }

  .settings-card__toggle {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    margin: 0;
  }

  .settings-card__checkbox {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 1.5px solid #d1d5db;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;
    flex-shrink: 0;
    background: #fff;
    margin-top: 1px;
  }

  .settings-card__checkbox:checked {
    background: var(--accent-color, #009be5);
    border-color: var(--accent-color, #009be5);
  }

  .settings-card__checkbox:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1px;
    width: 6px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .settings-card__checkbox:hover {
    border-color: #9ca3af;
  }

  .settings-card__row-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .settings-card__row-title {
    font-size: 13px;
    font-weight: 600;
    color: #404040;
  }

  .settings-card__row-desc {
    font-size: 12px;
    color: #737373;
    line-height: 1.4;
  }

  .aggrement-error {
    color: #dc2626;
    font-size: 12px;
    margin-top: 4px;
    padding-left: 30px;
  }
</style>
